import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const PublicTicketSubmission = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    pppoe_id: searchParams.get('pppoe_id') || '',
    issue_category: '',
    description: '',
    contact_phone: '',
    contact_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customerInfo, setCustomerInfo] = useState(null);

  const ISSUE_CATEGORIES = [
    'No Internet',
    'Red Light No Internet',
    'Slow Speed',
    'Router Problem',
    'Fiber Cut',
    'New Installation',
    'Billing Question',
    'ONU Issue',
    'Other'
  ];

  useEffect(() => {
    if (formData.pppoe_id) {
      lookupCustomer();
    }
  }, []);

  const lookupCustomer = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/public/customers/lookup?pppoe_id=${encodeURIComponent(formData.pppoe_id)}`);
      if (response.ok) {
        const data = await response.json();
        setCustomerInfo(data);
        setFormData(prev => ({
          ...prev,
          contact_phone: data.phone || '',
          contact_name: data.name || ''
        }));
      } else {
        setError('Customer not found. Please check the PPPoE ID.');
      }
    } catch (err) {
      setError('Error looking up customer: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.pppoe_id || !formData.issue_category) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/public/tickets/submit-public', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pppoe_id: formData.pppoe_id,
          issue_category: formData.issue_category,
          description: formData.description,
          contact_phone: formData.contact_phone,
          contact_name: formData.contact_name,
          source: 'onu_qr_code'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(`Ticket submitted successfully! Ticket ID: ${data.ticket_id}`);
        setFormData({
          pppoe_id: '',
          issue_category: '',
          description: '',
          contact_phone: '',
          contact_name: ''
        });
      } else {
        const error = await response.json();
        setError(error.message || 'Failed to submit ticket');
      }
    } catch (err) {
      setError('Error submitting ticket: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Report Issue</h1>
          <p className="text-gray-600 text-sm mt-2">Submit a service ticket via ONU QR code</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PPPoE ID *
            </label>
            <input
              type="text"
              value={formData.pppoe_id}
              onChange={(e) => setFormData({ ...formData, pppoe_id: e.target.value })}
              onBlur={lookupCustomer}
              placeholder="e.g., 2418.ygn027@ml.net"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!!searchParams.get('pppoe_id')}
            />
          </div>

          {customerInfo && (
            <div className="bg-blue-50 p-3 rounded border border-blue-200">
              <p className="text-sm"><strong>Name:</strong> {customerInfo.name}</p>
              <p className="text-sm"><strong>Phone:</strong> {customerInfo.phone}</p>
              <p className="text-sm"><strong>Plan:</strong> {customerInfo.plan}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Issue Category *
            </label>
            <select
              value={formData.issue_category}
              onChange={(e) => setFormData({ ...formData, issue_category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select an issue...</option>
              {ISSUE_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the issue (optional)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Your Name
            </label>
            <input
              type="text"
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              placeholder="Your name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Phone
            </label>
            <input
              type="tel"
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              placeholder="Your phone number"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition"
          >
            {loading ? 'Submitting...' : 'Submit Ticket'}
          </button>
        </form>

        <p className="text-center text-xs text-gray-500 mt-4">
          Our support team will contact you shortly
        </p>
      </div>
    </div>
  );
};

export default PublicTicketSubmission;
